import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../../components/ui/ConfirmModal.jsx';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import AiMark from '../../components/ui/AiMark.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { Facts, HowThisPageWorks, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { fmtDateTime } from '../../utils/format.js';
import {
  ADDED_HOW, AUTH_SOURCE, DECISION, FORWARD_TYPE, KIND, MATCH, SEVERITY_TONE, defang, defangText, fmtBytes,
  AnswerCard, currentAnswer, plainOf, toldText, whoOf,
} from './inboxShared.jsx';
import { VerifiedSeal } from '../../components/ui/VerifiedSeal.jsx';
import Findings from './Findings.jsx';
import Investigation from './Investigation.jsx';

const ASKS = {
  payment: 'pay', bank_change: 'pay to new bank details', booking: 'book something', credentials: 'sign in or give a password or code',
  personal_data: 'give personal details', download: 'open or download a file', call: 'call a number',
};
const RISK_KIND = { scam: 'a scam', phishing: 'phishing', spam: 'spam', genuine: 'genuine' };
const PATTERN = {
  attendee_list_sale: 'Selling the attendee list', housing: 'Hotel or housing offer', directory_listing: 'Paid directory listing',
  overdue_invoice: '“Overdue invoice” demand', credential_phish: 'Login or account phishing', gift_card: 'Gift card request',
  payment_redirect: 'New bank details for a payment', other_scam: 'Another kind of scam',
};

/** The AI's 0–100 scam/phishing rating as a small bar. */
function RiskMeter({ score, kind }) {
  const tone = score >= 70 ? 'bad' : score >= 40 ? 'warn' : 'good';
  return (
    <span className="risk-meter" title="The AI’s opinion only. Our own checks decide the verdict; from 40 it labels an unproven email a likely scam.">
      <span className={`risk-bar risk-${tone}`} role="img" aria-label={`Risk ${score} out of 100`}><span style={{ width: `${score}%` }} /></span>
      <strong>{score}/100</strong> <span className="muted">likely {RISK_KIND[kind] || 'a scam'}</span>
    </span>
  );
}

/** How an image in the email compares with your logos. */
function logoVerdict(l) {
  if (!l) return { tone: 'neutral', label: 'Not your logo', why: 'Nothing like any logo you’ve uploaded.' };
  const sure = l.via === 'fingerprint' || (l.confidence ?? 0) >= 60;
  if (l.relation === 'same' && l.logo_status === 'current') return { tone: 'info', label: 'Your current logo', why: `${l.logo_of}’s logo. Anyone can copy a logo, so this proves nothing on its own.` };
  if (l.relation === 'same') return { tone: 'bad', label: 'Your previous logo', why: `${l.logo_of}’s old logo. Scammers often copy one from an old email.` };
  if (!sure) return { tone: 'neutral', label: 'Looks a little like yours', why: 'The AI wasn’t sure enough to say.' };
  return {
    tone: 'bad', label: l.relation === 'altered' ? 'Altered copy of your logo' : 'Another version of your logo',
    why: `Compared by AI with ${l.logo_of}’s logo${l.differences && l.differences !== 'none' ? `: ${l.differences}` : ''}.`,
  };
}

const AV = {
  clean: { tone: 'good', label: 'No virus found' },
  infected: { tone: 'bad', label: 'Virus found' },
  error: { tone: 'warn', label: 'Couldn’t scan' },
  not_scanned: { tone: 'neutral', label: 'Not scanned' },
};

const DECIDE = {
  official: {
    title: 'This email is official',
    submit: 'Mark as official',
    description: 'It really is from you. Tell us what was missing from your records, so the next one is recognised.',
  },
  scam: {
    title: 'This email is a scam',
    submit: 'Confirm scam',
    description: 'Its sender, links, phone numbers and bank details go into the shared scam database straight away, so the same scam is caught at every event we protect. We tell the person who forwarded it to delete it.',
  },
  phishing: {
    title: 'This email is phishing',
    submit: 'Confirm phishing',
    description: 'A scam after logins, passwords or card details. Reported like any scam, and we tell the person who forwarded it to change their password if they used its links.',
  },
  not_related: {
    title: 'Nothing to do with us',
    submit: 'Save',
    description: 'It isn’t from you and isn’t pretending to be you — it was sent to your check address by mistake. Nothing is reported. We tell the person who forwarded it to check with whoever really sent it.',
  },
  unsure: { title: 'Not sure yet', submit: 'Save', description: 'It stays in “needs a decision” so someone can look again. The person who forwarded it isn’t told anything yet.' },
};

const APPROACH_DECIDE = {
  scam: { title: 'It was a scam', description: 'The numbers you tick are confirmed as a scam everywhere we protect, and the person who reported it is told. Your summary is what people see when they check one of those numbers.' },
  phishing: { title: 'It was phishing', description: 'A scam after logins, passwords or card details. Confirmed like any scam; your summary is shown when someone checks the number.' },
  official: { title: 'It was really us', description: 'We tell the person who reported it that the call or message was genuine.' },
  not_related: { title: 'Nothing to do with us', description: 'It wasn’t from you and wasn’t pretending to be you. Nothing is reported.' },
};
const APPROACH_CHOICES = [
  { key: 'scam', label: 'Phone or text scam', why: 'Pretending to be us, e.g. selling rooms, lists or stands', tone: 'bad' },
  { key: 'phishing', label: 'Phishing', why: 'Pretending to be us to get logins, codes or card details', tone: 'bad' },
  { key: 'official', label: 'It was us', why: 'One of our team, or someone working for us', tone: 'good' },
  { key: 'not_related', label: 'Not about us', why: 'Nothing to do with us or our events', tone: 'neutral' },
];

// The answers in everyday words (the keys are what the API takes)
const PLAIN_ANSWER = { scam: 'It’s a scam', phishing: 'It’s phishing', official: 'It’s really from us', not_related: 'Nothing to do with us' };

// The choices, in the order a busy organiser reads them
const CHOICES = [
  { key: 'scam', label: 'Scam', why: 'Pretending to be us, e.g. fake invoices, hotels or ticket offers', tone: 'bad' },
  { key: 'phishing', label: 'Phishing', why: 'Pretending to be us to steal logins or card details', tone: 'bad' },
  { key: 'official', label: 'Official', why: 'It really is from us, or from someone working for us', tone: 'good' },
  { key: 'not_related', label: 'Not about us', why: 'Genuine email from someone else, sent here by mistake', tone: 'neutral' },
];

/** What the person who forwarded it has been told so far. */
function replyText(r) {
  if (!r.status) return 'Not sent yet';
  if (r.status === 'skipped') return `Not sent: ${r.note || 'we only reply when we can confirm who forwarded it'}. We only answer people whose own email we can confirm, so we can’t be used to email strangers.`;
  if (r.status === 'failed') return `Couldn’t be sent${r.note ? `: ${r.note}` : ''}.`;
  const first = r.kind === 'holding' ? 'told not to act on it until you decide' : 'given our verdict';
  const then = r.outcome ? ` Then told your decision (${DECISION[r.outcome.told]?.short.toLowerCase()})${r.outcome.status === 'failed' ? ', but that email couldn’t be sent' : ''} ${fmtDateTime(r.outcome.at)}.` : r.kind === 'holding' ? ' They’re waiting for your decision.' : '';
  return `Sent ${fmtDateTime(r.at)}: ${first}.${then}`;
}

/** Sent to a contact planted in a list the company shared: what that means. */
function HoneypotBanner({ h, eventId }) {
  const listLink = h.planting_id && eventId ? <Link to={`/events/${eventId}/plantings/${h.planting_id}`}>the list you gave to {h.given_to}</Link> : <>a list you shared</>;
  if (h.expected) {
    return (
      <Alert tone="info">
        <strong>Sent to a planted contact by the people you gave the list to.</strong> {h.name || h.address} only exists in {listLink}, and this came
        from their own domain (proven), so it&rsquo;s the use you expected. Nothing to do.
      </Alert>
    );
  }
  return (
    <Alert tone="danger">
      <strong>Your list has travelled.</strong> This was sent to {h.name ? `${h.name} (${h.address})` : h.address}, a made-up contact that exists only
      in {listLink}{h.planted_at ? ` (${fmtDateTime(h.planted_at)})` : ''}. Whoever sent it got the list — from them, or from someone they passed it to.
      We&rsquo;ve marked the sender as a confirmed scam everywhere we protect, and never replied, so they don&rsquo;t know it was a trap.
    </Alert>
  );
}

// A UK number as people write it (020 7946 0555, 07700 900123); anything else as stored
const fmtPhone = (e164) => {
  const v = String(e164 || '');
  if (!v.startsWith('+44')) return v;
  const n = `0${v.slice(3)}`;
  return /^02/.test(n) ? `${n.slice(0, 3)} ${n.slice(3, 7)} ${n.slice(7)}` : `${n.slice(0, 5)} ${n.slice(5)}`;
};

/** For a reported call or text confirmed as a scam: what people checking the number see. */
function ApproachFields({ ap, value, onChange, errors }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const toggle = (n) => set('phones', value.phones.includes(n) ? value.phones.filter((x) => x !== n) : [...value.phones, n]);
  return (
    <>
      <Field label="How they were contacted" as="select" value={value.channel} onChange={(e) => set('channel', e.target.value)} error={errors.channel}>
        {Object.entries(ap.channels).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
      </Field>
      <fieldset className="fieldset">
        <legend>The scammer’s numbers</legend>
        {ap.caller_numbers?.length > 0 ? (
          <ul className="check-list">
            {ap.caller_numbers.map((n) => (
              <li key={n}><label className="checkbox"><input type="checkbox" checked={value.phones.includes(n)} onChange={() => toggle(n)} /> <span className="mono">{fmtPhone(n)}</span></label></li>
            ))}
          </ul>
        ) : <p className="muted small">We didn’t find a number in the report.</p>}
        <Field label="Another number" optional value={value.extra} onChange={(e) => set('extra', e.target.value)} placeholder="020 7946 0999" error={errors.phones}
          hint={!errors.phones ? 'Only numbers the scammer used — never the number of the person who reported it.' : undefined} />
      </fieldset>
      <Field label="What people checking the number will see" as="textarea" rows={3} maxLength={500} counter required value={value.summary}
        onChange={(e) => set('summary', e.target.value)} error={errors.summary}
        hint={!errors.summary ? 'One or two sentences, no names of the person who reported it. e.g. “Callers claiming to be our housing team ask for card details to hold hotel rooms.”' : undefined} />
      <label className="checkbox">
        <input type="checkbox" checked={value.publish} onChange={(e) => set('publish', e.target.checked)} />
        <span>Show this on the verify page when someone checks one of these numbers</span>
      </label>
    </>
  );
}

function DecisionModal({ kind, data, base, onClose, onDone }) {
  const ap = data.approach;
  const scamApproach = !!ap && (kind === 'scam' || kind === 'phishing');
  const [approach, setApproach] = useState(() => ({
    channel: ap?.report?.channel || ap?.channel || 'phone',
    phones: ap?.report?.phones || ap?.caller_numbers || [],
    extra: '',
    summary: ap?.report?.summary || ap?.summary || '',
    publish: ap?.report ? ap.report.published : true,
  }));
  const [fieldErrors, setFieldErrors] = useState({});
  const addable = kind === 'official' && !ap ? data.findings.filter((f) => f.addable) : [];
  const [picked, setPicked] = useState(() => Object.fromEntries(addable.map((f) => [f.id, { on: true, to: 'company' }])));
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const d = DECIDE[kind];

  const save = async () => {
    setBusy(true); setError('');
    try {
      const add = Object.entries(picked).filter(([, v]) => v.on).map(([id, v]) => ({ finding_id: Number(id), to: v.to }));
      const body = { decision: kind, note: note || undefined, add };
      if (scamApproach) {
        const errs = {};
        if (approach.summary.trim().length < 10) errs.summary = 'Write a sentence people checking the number will see.';
        if (!approach.phones.length && !approach.extra.trim()) errs.phones = 'Tick or add at least one number the scammer used.';
        if (Object.keys(errs).length) { setFieldErrors(errs); setBusy(false); return; }
        body.approach = { channel: approach.channel, summary: approach.summary.trim(), publish: approach.publish,
          phones: [...approach.phones, ...(approach.extra.trim() ? [approach.extra.trim()] : [])] };
      }
      const res = await api(`${base}/decision`, { method: 'POST', body });
      onDone(res);
    } catch (err) {
      setFieldErrors(err.data?.fields || {});
      setError(err.data?.fields ? '' : err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title={ap ? (APPROACH_DECIDE[kind]?.title || d.title) : d.title} description={ap ? (APPROACH_DECIDE[kind]?.description || d.description) : d.description} onClose={onClose} onSubmit={save} busy={busy} size={addable.length ? 'lg' : 'md'} tone={kind === 'scam' || kind === 'phishing' ? 'danger' : undefined}
      footer={<ModalActions submitLabel={d.submit} busyLabel="Saving…" busy={busy} onCancel={onClose} danger={kind === 'scam' || kind === 'phishing'} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      {scamApproach && <ApproachFields ap={ap} value={approach} onChange={(v) => { setApproach(v); setFieldErrors({}); }} errors={fieldErrors} />}
      {(kind === 'official' || kind === 'not_related') && data.analysis?.verdict === 'scam' && (
        <Alert tone="warn">We called this a scam. If it’s really yours, we’ll look at why, so our checks get it right next time.</Alert>
      )}
      {addable.length > 0 && (
        <fieldset className="fieldset">
          <legend>Add to your records</legend>
          <ul className="check-list">
            {addable.map((f) => (
              <li key={f.id}>
                <label className="checkbox">
                  <input type="checkbox" checked={picked[f.id].on} onChange={(e) => setPicked({ ...picked, [f.id]: { ...picked[f.id], on: e.target.checked } })} />
                  <span>{f.addable.label} <span className="muted small">({KIND[f.kind].toLowerCase()})</span></span>
                </label>
                {picked[f.id].on && data.message.event && (
                  <select className="inline-select" aria-label={`Add ${f.addable.label} to`} value={picked[f.id].to}
                    onChange={(e) => setPicked({ ...picked, [f.id]: { ...picked[f.id], to: e.target.value } })}>
                    <option value="company">for the whole company</option>
                    <option value="event">for {data.message.event.name} only</option>
                  </select>
                )}
              </li>
            ))}
          </ul>
          {data.findings.some((f) => f.kind === 'bank' && f.match !== 'theirs') && (
            <p className="muted small">Bank details can’t be added from here (we only keep a fingerprint of them): add them on your company or event page.</p>
          )}
        </fieldset>
      )}
      <Field label="Note" optional hint="Only your team sees it — e.g. why you decided this." as="textarea" rows={2} maxLength={1000} counter value={note} onChange={(e) => setNote(e.target.value)} />
    </Modal>
  );
}

function FindingValue({ f }) {
  if (f.kind === 'link') {
    return (
      <>
        <span className="mono break">{defang(f.display)}</span>
        {f.text && f.text_mismatch && <div className="muted small">Shown as “{f.text}”</div>}
        {f.wrapped && <div className="muted small">Hidden behind a link-scanning service (like Outlook Safe Links). This is where it really goes.</div>}
      </>
    );
  }
  return <span className={f.kind === 'bank' ? '' : 'mono break'}>{f.display}</span>;
}

export default function InboxMessage() {
  const { messageId } = useParams();
  const { organisation } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [deciding, setDeciding] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.notice || '');
  const base = organisation ? `/portal/organisations/${organisation.id}/inbox/${messageId}` : '';

  const load = useCallback(async () => {
    if (!base) return;
    try { setData(await api(base)); } catch (err) { setError(err.message); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  // Still being checked: look again shortly
  useEffect(() => {
    if (!data || data.message.verdict || ['failed', 'rejected'].includes(data.message.status)) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [data, load]);

  const [rechecking, setRechecking] = useState(false);
  const recheck = async () => {
    setRechecking(true); setNotice('');
    try {
      await api(`${base}/recheck`, { method: 'POST' });
      setNotice('Checking again against your current records…');
      setTimeout(async () => { await load(); setNotice(''); setRechecking(false); }, 3000);
    } catch (err) { setNotice(err.message); setRechecking(false); }
  };

  if (error) return <div className="page"><PageHeader back={{ to: '/inbox', label: 'Messages' }} title="Email" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;
  const { message: m, original: o, analysis, findings, attachments, decisions, ai, sending_source: src } = data;

  const plain = plainOf(m);
  const isCall = m.report_kind === 'approach';
  const decided = m.decision && m.decision !== 'unsure';
  const settled = !decided && ['scam', 'verified', 'likely_genuine'].includes(m.verdict);
  const choices = isCall ? APPROACH_CHOICES : CHOICES;

  return (
    <div className="page">
      <div className="page-header-top"><Link to="/inbox" className="back-link">← Messages</Link><HowThisPageWorks topic="messages" /></div>

      {notice && <Alert tone="success">{notice}</Alert>}

      <section className="card ld-hero" aria-labelledby="msg-h">
        <div className="ld-tags">
          <span className={`msg-pill tone-${plain.tone}`}>{plain.label}</span>
          <span className="muted">{whoOf(m)}{m.event ? ` · ${m.event.name}` : ''} · {fmtDateTime(m.received_at)}</span>
        </div>
        <h1 id="msg-h" className="ld-question">{o?.subject || (isCall ? 'A call or message someone reported' : '(no subject)')}</h1>

      </section>

      {analysis && <Findings analysis={analysis} recheck={recheck} rechecking={rechecking} canRecheck={data.can_decide} />}
      {!m.verdict && !['failed', 'rejected'].includes(m.status) && <p className="muted">We’re still checking it. This page updates by itself.</p>}
      {data.scam_match && data.scam_match.similarity >= 0.55 && (
        <Alert tone={data.scam_match.status === 'confirmed' ? 'danger' : 'warn'}>
          <strong>Same as a known {data.scam_match.kind === 'phishing' ? 'phishing email' : 'scam'}:</strong> its wording is {Math.round(data.scam_match.similarity * 100)}% the
          same as one {data.scam_match.status === 'confirmed' ? 'already confirmed' : 'flagged (not yet confirmed)'} at an event we protect, sent from a different address, often with the event’s name swapped.
          {data.scam_match.status === 'confirmed' ? ' Every scam you confirm helps catch the next copy.' : ''}
        </Alert>
      )}

      {(m.reply || (data.can_decide && m.verdict && !m.honeypot) || (!data.can_decide && m.verdict && !m.decision && !['verified', 'likely_genuine'].includes(m.verdict))) && (
      <section className="card ld-hero msg-answer-card" aria-label="Your answer">
        {m.reply && (
          <div className="msg-told">
            <p className="msg-told-title">What we’ve told the person who asked</p>
            <p className="muted">{toldText(m.reply) || replyText(m.reply)}</p>
          </div>
        )}

        {data.can_decide && m.verdict && !m.honeypot && (
          <div className="ld-decide">
            <h2>{decided ? 'You’ve answered this one' : settled ? 'Nothing for you to do, unless we got it wrong' : isCall ? 'Was this call or message from you?' : 'Is this email from you?'}</h2>
            {decided && <p className="muted">You said <strong>{DECISION[m.decision].short.toLowerCase()}</strong>. Changed your mind? Choose again and we’ll pass the correction on.</p>}
            <div className={`msg-answers${currentAnswer(m) ? ' has-current' : ''}`}>
              {choices.map((c) => (
                <AnswerCard key={c.key} k={c.key} title={PLAIN_ANSWER[c.key] || c.label} what={c.why} current={currentAnswer(m)}
                  disabled={m.decision === c.key} onClick={() => setDeciding(c.key)} />
              ))}
            </div>
            {!decided && !settled && m.decision !== 'unsure' && (
              <button type="button" className="link-button small" onClick={() => setDeciding('unsure')}>Not sure yet? Leave it for someone else</button>
            )}
          </div>
        )}
        {!data.can_decide && m.verdict && !m.decision && !['verified', 'likely_genuine'].includes(m.verdict) && <p className="muted">An owner or admin of your company needs to answer this one.</p>}
      </section>
      )}

      {m.verdict && <Investigation organisationId={organisation.id} messageId={m.id} key={`${m.decision}-${m.verdict}`} />}

      {m.also_reported > 0 && (
        <Alert tone="warn">Reported {m.also_reported + 1} times: {m.also_reported} more {m.also_reported === 1 ? 'person' : 'people'} sent us the same email (forwarded it, or checked it on your check page). Your answer here answers all of them.</Alert>
      )}
      {m.verify_page && (
        <Alert tone="info">
          Someone checked this on your check page, and it showed something you don’t already know about, so it’s here for you to answer.
          {m.reply?.status === 'sent' ? ' They left an email address, so your answer is sent to them.' : ' They didn’t leave an email address, so nobody is told your answer.'}
        </Alert>
      )}
      {m.added && (
        <Alert tone="info">
          Added by {m.added.by || 'your team'}{m.added.type === 'approach' ? ' as a call or message' : ''}. Nobody forwarded it, so nobody is replied to.
          {' '}Your answer adds it to the scams we catch at every event.
          {m.added.format === 'msg' && !m.added.kept_headers && ' Outlook didn’t keep this email’s original headers, so where it came from can’t be checked. An .eml copy or its source shows more.'}
          {m.added.others_in_file > 0 && ` The file held ${m.added.others_in_file + 1} emails; only the first was added.`}
        </Alert>
      )}
      {m.honeypot && <HoneypotBanner h={m.honeypot} eventId={m.event?.id} />}
      {m.status === 'rejected' && <Alert tone="warn">{m.error || 'Not checked.'}</Alert>}
      {m.status === 'failed' && <Alert tone="danger">We couldn’t read this email. {m.error}</Alert>}

      <details className="ld-tech" open={location.hash === '#technical'}>
        <summary>The email itself and the technical checks</summary>
        <div className="ld-tech-body">



      {data.approach && (
        <Section title="What they told us"
          description="Someone wrote to report a call or message, not to forward an email. This is what they said happened. Numbers and names can be faked, so we check them against your records but can never prove who it was.">
          <Facts items={[
            ['How', data.approach.channels[data.approach.channel] || 'Another way'],
            ['Their number', data.approach.caller_numbers?.length ? data.approach.caller_numbers.map((n) => <div key={n} className="mono">{fmtPhone(n)}</div>) : <span className="muted">None given</span>],
            ['Name they gave', data.approach.caller_name || <span className="muted">None given</span>],
            data.approach.claimed_org && ['Said they were from', data.approach.claimed_org],
            data.approach.asked_for?.length > 0 && ['Asked for', data.approach.asked_for.map((a) => ({ payment: 'money or card details', booking: 'a booking', personal_data: 'passwords, codes or personal details', buy_data: 'to buy a contact list' })[a] || a).join(', ')],
            data.approach.summary && [<><AiMark /> In short</>, data.approach.summary],
            data.approach.report && ['Your write-up', <>{data.approach.report.summary}<div className="muted small">{data.approach.report.published ? 'Shown on the verify page for these numbers' : 'Not shown publicly'}</div></>],
          ]} />
        </Section>
      )}

      {ai && (ai.summary || ai.asks_for?.length || ai.signature || ai.risk_score != null) && (
        <Section title={<><AiMark label /> What it says</>} description="Read by AI to save you reading it all. Its risk score can label an email a likely scam when nothing proves it’s yours, but it never makes something a definite scam on its own — and every name, number or quote it picked out was checked against the email word for word.">
          <Facts items={[
            ai.risk_score != null && [<><AiMark /> Scam risk</>, <>
              <RiskMeter score={ai.risk_score} kind={ai.risk_kind} />
              {ai.risk_signals?.length > 0 && <ul className="plain-list small">{ai.risk_signals.map((x) => <li key={x}>{x}</li>)}</ul>}
            </>],
            ai.scam_pattern && ['Looks like', <>{PATTERN[ai.scam_pattern] || ai.scam_pattern}{ai.pattern_evidence && <span className="muted"> — “{ai.pattern_evidence}”</span>}</>],
            ai.summary && ['Summary', ai.summary],
            ai.asks_for?.length > 0 && ['Asks you to', ai.asks_for.map((a) => ASKS[a] || a).join(', ')],
            ai.urgency && ai.urgency !== 'low' && ['Pressure to act', ai.urgency === 'high' ? <Pill tone="bad">High</Pill> : <Pill tone="warn">Some</Pill>],
            ai.claims_to_be && ['Claims to be', ai.claims_to_be],
            ai.signature && (ai.signature.name || ai.signature.company) && ['Signed', [ai.signature.name, ai.signature.job_title, ai.signature.company].filter(Boolean).join(', ')],
          ]} />
        </Section>
      )}

      {o && (
        <Section title={m.report_kind === 'question' ? 'Their question' : m.report_kind === 'approach' ? 'Their message' : 'The email'}
          description={m.report_kind === 'question' || m.report_kind === 'approach'
            ? 'Written to your check address by the person asking. We judge only what it’s about — never who sent it.' : undefined}
          actions={(
          <span className="head-buttons">
            {o.has_html && <Link to={`/inbox/${m.id}/view`} className="btn btn-secondary btn-small">See it as it looked</Link>}
            <Link to={`/inbox/${m.id}/headers`} className="btn btn-secondary btn-small">Headers and route</Link>
            {data.can_decide && o.has_raw && <a href={`/api${base}/original.eml`} className="btn btn-secondary btn-small" download>Download original</a>}
          </span>
        )}>
          <Facts items={[
            ['From', o.from_address ? <span className="break">{o.from_name ? `${o.from_name} ` : ''}<span className="mono">&lt;{o.from_address}&gt;</span></span> : '—'],
            o.reply_to && ['Replies go to', <span className="mono break">{o.reply_to}</span>],
            ['To', o.to || '—'],
            ['Sent', o.sent_at ? fmtDateTime(o.sent_at) : (o.sent_at_text || '—')],
            ['How it reached us', m.honeypot
              ? <>Sent straight to <span className="mono">{m.honeypot.address}</span>, a contact you planted <span className="muted">· {m.event?.name || 'your list'}</span></>
              : m.verify_page
              ? <>Checked on your verify page{m.event && <span className="muted"> · {m.event.name}</span>}</>
              : m.added
              ? <>Added in the portal{m.added.by && <> by {m.added.by}</>} <span className="muted">· {ADDED_HOW[m.added.type]}{m.added.filename ? ` (${m.added.filename})` : ''}{m.event && ` · ${m.event.name}`}</span></>
              : m.via_other_event
              ? <>Reported to another event protected by SummitShield <span className="muted">· it uses or imitates your domain</span></>
              : <>{FORWARD_TYPE[o.forward_type]}{m.forwarder_domain && <span className="muted"> · from someone at @{m.forwarder_domain}</span>}{m.event && <span className="muted"> · {m.event.name}</span>}</>],
            ['Who really sent it', o.forward_type === 'inline'
              ? <span className="muted">We can&rsquo;t tell: {m.added ? 'only its text was added' : 'it was forwarded as text'}, which leaves out the details we check.</span>
              : (
                <>
                  {o.origin_proven
                    ? <span className="title-with-pill"><VerifiedSeal size={16} /> <strong>Confirmed</strong> — it really came from {o.from_address?.split('@')[1] || 'the sender’s domain'}.</span>
                    : <><strong>Not confirmed</strong> — {o.dmarc === 'fail' ? 'it failed the check that proves where an email came from.' : 'we can’t prove who really sent it.'}</>}
                  <div className="muted small">{AUTH_SOURCE[o.auth_source]} Senders can fake the “From” name; this looks at hidden details they can&rsquo;t fake.{o.service ? ` Sent through ${o.service}.` : ''}</div>
                </>
              )],
            src?.configured && o.forward_type !== 'inline' && ['Email service', src.matched
              ? <><Pill tone="info">One of yours</Pill> {src.matched.name}</>
              : <><Pill tone="warn">Not one of yours</Pill> {o.service || 'An unrecognised service'}. <span className="muted small">Real email from you normally comes through the services listed on <Link to="/company/email-services">Your email services</Link>.</span></>],
            m.reply && ['Our emails to the person who forwarded it', replyText(m.reply)],
          ]} />
          <h3 className="subhead">Message</h3>
          <pre className="email-body">{defangText(o.text_body) || '(empty)'}</pre>
          <p className="muted small">Web addresses are shown with [.] in them so nothing here can be clicked by accident.</p>
        </Section>
      )}

      {findings.length > 0 && (
        <Section title="Everything it mentions" description="Every address, link, phone number and bank account in it, checked against your records and the scams confirmed at every event we protect. Point at a result to see what it means." flush>
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>What</th><th>Detail</th><th>Result</th></tr></thead>
              <tbody>
                {findings.map((f) => (
                  <tr key={f.id}>
                    <td data-label="What">{KIND[f.kind]}</td>
                    <td data-label="Detail"><FindingValue f={f} /></td>
                    <td data-label="Result">
                      {f.match ? <Pill tone={MATCH[f.match].tone} title={MATCH[f.match].why}>{MATCH[f.match].label}</Pill> : <span className="muted">—</span>}
                      {f.lookalike_of
                        ? <div className="muted small result-why">Made to look like {f.lookalike_of}.</div>
                        : f.match && <div className="muted small result-why">{MATCH[f.match].why}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {attachments.length > 0 && (
        <Section title="Attachments" description="Never opened by us or shown here. Download the original only if you need to investigate." flush>
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>File</th><th>Type</th><th>Size</th><th>Virus scan</th><th>Warnings</th></tr></thead>
              <tbody>
                {attachments.map((a) => (
                  <tr key={a.id}>
                    <td data-label="File" className="break">{a.filename || '(no name)'}</td>
                    <td data-label="Type">{a.content_type || '—'}</td>
                    <td data-label="Size">{fmtBytes(a.size_bytes)}</td>
                    <td data-label="Virus scan">{AV[a.av_result] ? <Pill tone={AV[a.av_result].tone} title={a.av_signature || undefined}>{AV[a.av_result].label}</Pill> : '—'}{a.av_signature && a.av_result === 'infected' && <div className="muted small">{a.av_signature}</div>}</td>
                    <td data-label="Warnings">{a.flags.length ? a.flags.map((fl) => <Pill key={fl} tone="bad">{{ macro: 'Macros (hidden programs)', html: 'Web page (can ask for passwords)', archive: 'Zip or archive (can hide files)', executable: 'Program', has_links: 'Contains links', virus: 'Virus' }[fl] || fl}</Pill>) : <span className="muted">None</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {data.images?.length > 0 && (
        <Section title="Images in it" description="Pictures inside the email, such as a signature logo, compared with the logos you’ve uploaded. Shown as copies we redrew, so nothing in them can run. Images on the web are never fetched.">
          <ul className="email-images">
            {data.images.map((im) => {
              const v = logoVerdict(im.logo);
              return (
                <li key={im.id}>
                  <div className="logo-frame logo-frame-small"><img src={im.url} alt={im.filename || 'An image from the email'} /></div>
                  <div>
                    <Pill tone={v.tone}>{v.label}</Pill>
                    <div className="muted small">{v.why}</div>
                  </div>
                </li>
              );
            })}
          </ul>
          {!data.images.some((im) => im.logo) && <p className="muted small">Upload your logo on the Company page (and any you used to use) so we can spot old or altered copies.</p>}
        </Section>
      )}

      {decisions.length > 0 && (
        <Section title="Decisions">
          <ul className="history">
            {decisions.map((d, i) => (
              <li key={i}>
                <Pill tone={DECISION[d.decision].tone}>{DECISION[d.decision].label.replace(/^You (said|confirmed): /, '')}</Pill>
                <span className="muted small"> {d.by ? `by ${d.by}, ` : ''}{fmtDateTime(d.decided_at)}</span>
                {d.note && <div className="small">{d.note}</div>}
              </li>
            ))}
          </ul>
        </Section>
      )}

        </div>
      </details>

      {data.can_delete && (
        <div className="test-delete">
          <button type="button" className="btn btn-danger-ghost btn-small" onClick={() => setDeleting(true)}>Delete this email</button>
          <span className="muted small">Platform admins only: removes it and everything it led to, including scam details only this email confirmed.</span>
        </div>
      )}
      {deleting && (
        <ConfirmModal title="Delete this email?" confirmLabel="Delete" danger onClose={() => setDeleting(false)}
          onConfirm={async () => { await api(base, { method: 'DELETE' }); navigate('/inbox', { replace: true, state: { notice: 'Deleted.' } }); }}>
          <p>It disappears from Inbox along with what we found, your decision, any report it made and scam details only it confirmed. Our replies already sent can’t be unsent.</p>
        </ConfirmModal>
      )}

      {deciding && (
        <DecisionModal kind={deciding} data={data} base={base} onClose={() => setDeciding(null)}
          onDone={(res) => {
            setDeciding(null);
            const passedOn = res.forwarder_told && res.decision !== 'unsure' ? ' We’re emailing your answer to the person who forwarded it.' : '';
            setNotice(res.added?.length ? `Saved. Added to your records: ${res.added.join(', ')}. Checking again…${passedOn}` : `Saved.${passedOn}`);
            load();
            if (res.added?.length) setTimeout(load, 3000);
          }} />
      )}
    </div>
  );
}
